import { useI18n } from '../../../i18n';
// ============================================
// ProfitByLocationTable - Location profit table
// ============================================

import { Pagination } from '../../../components/shared';
import { useClientPagination } from '../../../hooks/useClientPagination';
import { cn } from '../../../utils/cn';
import { useMoney } from '../../../providers/CurrencyProvider';

export interface ProfitByLocationRow {
  location: string;
  transactions: number;
  revenue: number;
  /** null = BE chưa cung cấp dữ liệu giá vốn — hiển thị "—". */
  profit: number | null;
  costPrice: number | null;
}

interface Props {
  rows: ProfitByLocationRow[];
  /** Số dòng mỗi trang. */
  pageSize?: number;
  className?: string;
}

export function ProfitByLocationTable({ rows, pageSize = 10, className }: Props) {
  const { t } = useI18n();
  const money = useMoney();
  const page = useClientPagination(rows, pageSize);

  // fmt chuyển vào trong component vì `useMoney()` là hook.
  const fmt = (v: number | null) =>
    v == null ? '—' : money.format(v, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

  return (
    <div className={cn('rounded-2xl bg-white p-6 shadow-sm', className)}>
      <h3 className="mb-4 text-base font-bold text-[#191B23]">{t('profitByLocation')}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#F1F5F9]">
              <th className="whitespace-nowrap pb-3 pr-2 text-left text-[10px] font-bold uppercase tracking-wide text-[#94A3B8]">{t('location')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('transactions')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('revenue')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('profit')}</th>
              <th className="whitespace-nowrap px-2 pb-3 text-right text-[10px] font-bold uppercase tracking-wide text-[#94A3B8] last:pr-0">{t('costPrice')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-sm text-slate-400">
                  {t('noDataInPeriod')}
                </td>
              </tr>
            )}
            {page.pageItems.map((row, i) => (
              <tr key={`${row.location}-${i}`} className="border-b border-[#F8FAFC] last:border-0">
                <td className="py-3 pr-2 font-bold text-[#191B23]">{row.location}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{row.transactions}</td>
                <td className="px-2 py-3 text-right text-[#434655]">{fmt(row.revenue)}</td>
                {/* Bug #80: nhấn mạnh đặt nhầm cột — LỢI NHUẬN mới là số cần
                    nổi bật, giá vốn chỉ là số phụ trợ. Trước đây ngược lại, và
                    ngược cả với TopProfitableProductsTable ngay bên cạnh. */}
                <td className="px-2 py-3 text-right font-bold text-[#16A34A]">{fmt(row.profit)}</td>
                <td className="py-3 pl-2 text-right text-[#434655]">{fmt(row.costPrice)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {page.hasItems && (
        <Pagination
          variant="numbered"
          currentPage={page.currentPage}
          totalPages={page.totalPages}
          totalItems={page.totalItems}
          pageSize={page.pageSize}
          onPageChange={page.setPage}
          itemLabel={t('stores')}
          className="mt-4 border-t border-[#F1F5F9] pt-4"
        />
      )}
    </div>
  );
}
